import {Box, HStack, IconButton, Text} from '@chakra-ui/react';
import {Trash2} from 'lucide-react';
import React, {FC} from 'react';

import {useDebouncedCartUpdate, useTranslation} from '../../../../hooks';
import {colors} from '../../../../theme';
import {ICartItem, IRestaurantResponse} from '../../../../types';
import {ProductCartControls} from '../product-cart-controls/ProductCartControls';

interface Props {
  item: ICartItem;
  provider?: IRestaurantResponse;
  onDelete: (itemId: string) => void;
}

export const CartItemRow: FC<Props> = ({item, provider, onDelete}) => {
  const {t} = useTranslation();
  const {localQuantity, handleAdd, handleRemove, isSelected} = useDebouncedCartUpdate({
    product: item.product,
    provider,
    initialQuantity: item.quantity,
    cartItemId: item.id,
    debounceDelay: 300,
  });

  const displayQuantity = localQuantity || item.quantity;

  return (
    <>
      <HStack justify="space-between" align="flex-start">
        <HStack spacing={2} align="baseline">
          {item.product.old_price ? (
            <>
              <Text fontSize="12px" color={colors.textGray} textDecoration="line-through">
                {(item.product.old_price * displayQuantity).toFixed(2)}
              </Text>
              <Text fontSize="16px" fontWeight="600" color={colors.pink}>
                {(item.product.price * displayQuantity).toFixed(2)} {t('CURRENCY')}
              </Text>
            </>
          ) : (
            <Text fontSize="16px" fontWeight="600" color="gray.800">
              {(item.price * displayQuantity).toFixed(2)} {t('CURRENCY')}
            </Text>
          )}
        </HStack>
        <IconButton
          icon={
            <Box display="flex" alignItems="center" justifyContent="center">
              <Trash2 size="18px" />
            </Box>
          }
          size="20px"
          variant="unstyled"
          color="black"
          aria-label="Remove"
          boxSize="30px"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(item.id);
          }}
          bg={colors.bodyBackground}
          borderRadius="full"
        />
      </HStack>

      <Text fontSize="12px" color={colors.textGray} flex={1} noOfLines={3} mt={1} wordBreak="break-word">
        {item.product?.name}
      </Text>

      {item.product?.measurement_display && (
        <Text color={colors.textGray} fontSize="12px" mt={1}>
          {item.product.measurement_display}
        </Text>
      )}

      <HStack mt={3} justify="space-between">
        <Box onClick={(e) => e.stopPropagation()}>
          <ProductCartControls
            isSmall
            isSelected={isSelected}
            quantity={displayQuantity}
            massMeasurement={item.product.mass_measurement}
            onAdd={handleAdd}
            onDelete={handleRemove}
            cartItemId={item.id}
            isWeightProduct={item.product.is_weighted_product}
          />
        </Box>
      </HStack>
    </>
  );
};
